/** Copia adaptada de apps/builder-spa/src/api/connectors.ts. */
import { apiFetch } from './apiClient.js';

export type ConnectorProvider = 'meta_native' | '360dialog' | 'twilio' | 'messagebird';
export type ConnectorStatus = 'connected' | 'error';

export interface OutboundConnector {
  id: string;
  tenantId: string;
  name: string;
  provider: ConnectorProvider;
  businessPhone?: string;
  wabaId?: string;
  phoneNumberId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
  apiKeySecretArn?: string;
  status: ConnectorStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface ListConnectorsResponse {
  items: OutboundConnector[];
}

export interface ConnectorCreate {
  name: string;
  provider: ConnectorProvider;
  businessPhone?: string;
  wabaId?: string;
  phoneNumberId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
  /** #159: credencial plain (write-only). */
  apiKey?: string;
  accessToken?: string;
  /** Back-compat: ARN pre-creado. */
  apiKeySecretArn?: string;
  status?: ConnectorStatus;
}

export type ConnectorPatch = Partial<
  Pick<
    OutboundConnector,
    | 'name'
    | 'businessPhone'
    | 'wabaId'
    | 'phoneNumberId'
    | 'displayPhoneNumber'
    | 'verifiedName'
    | 'apiKeySecretArn'
    | 'status'
  >
> & {
  /** #159: rotación. */
  apiKey?: string;
  accessToken?: string;
};

export const SECRET_ARN_REGEX =
  /^arn:aws:secretsmanager:[a-z0-9-]+:\d{12}:secret:[a-zA-Z0-9/_+=.@-]+$/;

export async function listConnectors(
  tenantId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<OutboundConnector[]> {
  const qs = opts.includeArchived ? '?includeArchived=true' : '';
  const res = await apiFetch<ListConnectorsResponse | OutboundConnector[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function createConnector(
  tenantId: string,
  body: ConnectorCreate,
): Promise<OutboundConnector> {
  return apiFetch<OutboundConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors`,
    { method: 'POST', body },
  );
}

export async function updateConnector(
  tenantId: string,
  connectorId: string,
  patch: ConnectorPatch,
  ifMatchVersion: number,
): Promise<OutboundConnector> {
  return apiFetch<OutboundConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveConnector(
  tenantId: string,
  connectorId: string,
): Promise<OutboundConnector> {
  return apiFetch<OutboundConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}/archive`,
    { method: 'POST' },
  );
}

export interface TestConnectorResult {
  ok: boolean;
  provider: string;
  templatesCount?: number;
  error?: string;
}

/** #160 — Health check Meta Graph / 360dialog usando el Secret SM persistido. */
export async function testConnector(
  tenantId: string,
  connectorId: string,
): Promise<TestConnectorResult> {
  return apiFetch<TestConnectorResult>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/connectors/${encodeURIComponent(connectorId)}/test`,
    { method: 'POST' },
  );
}
